<script setup lang="ts">
import errorIcon from "@/assets/icons/times.svg?raw";
import infoIcon from "@/assets/icons/info.svg?raw";
import successIcon from "@/assets/icons/check.svg?raw";
import warningIcon from "@/assets/icons/alert.svg?raw";
import { useToastsStore, type Toast } from "@/stores/toasts";
import { computed, onBeforeUnmount, onMounted, ref } from "vue";

const props = defineProps<Toast>();

let durationTimer = -1;

const animationPlayState = ref("paused");

const toastsStore = useToastsStore();

const icon = computed(() => {
  let icon = "";
  switch (props.type) {
    case "success": {
      icon = successIcon;
      break;
    }
    case "error": {
      icon = errorIcon;
      break;
    }
    case "info": {
      icon = infoIcon;
      break;
    }
    case "warning": {
      icon = warningIcon;
      break;
    }
    default: {
      icon = infoIcon;
      break;
    }
  }
  return icon
    .replaceAll(/fill="[^"]*"/g, "")
    .replace(/width="\d+"/, "")
    .replace(/height="\d+"/, "");
});

function onDismiss() {
  cancelDurationTimer();
  toastsStore.dismissToast(props.id);
}
function cancelDurationTimer() {
  if (durationTimer !== -1) {
    clearInterval(durationTimer);
  }
}

onMounted(() => {
  if (props.duration !== undefined && props.duration !== Infinity) {
    durationTimer = window.setInterval(() => {
      onDismiss();
    }, props.duration * 1000);
    animationPlayState.value = "running";
  }
});
onBeforeUnmount(() => {
  cancelDurationTimer();
});
</script>

<template>
  <div class="toast" :class="props.type">
    <div class="message">
      <div class="icon-wrapper">
        <div class="countdown" v-if="props.duration !== undefined && props.duration !== Infinity">
          <div class="background" />
          <div
            class="foreground"
            :style="{
              animationPlayState,
              animationDuration: `${props.duration ?? 0}s`,
            }"
          />
        </div>
        <div class="icon" v-html="icon"></div>
      </div>
      {{ props.message }}
      <span class="count" v-if="props.count && props.count > 1">(x{{ props.count }})</span>
    </div>
    <div v-if="props.link !== undefined" class="link">
      <RouterLink :to="props.link.to">{{ props.link.label }}</RouterLink>
    </div>
    <div class="actions" v-if="props.dismissible">
      <button @click="onDismiss">dismiss</button>
    </div>
  </div>
</template>

<style scoped>
.toast {
  /* toast */
  --toast-background-color: var(--background-inverse);
  --toast-stripe-color: color-mix(in srgb, var(--background-inverse) 90%, white 10%);
  --toast-border-color: var(--border-inverse);
  --toast-text-color: var(--content-primary-inverse);
  --toast-dismiss-color: var(--content-secondary-inverse);
  --toast-shadow-elevation: rgb(0 0 0 / 48%);
  --toast-shadow-inset: inset 0 0 1.1px 2px rgb(255 255 255 / 13%);

  display: flex;
  align-items: center;
  padding: var(--numbers-12) var(--numbers-20);
  border: 2px solid var(--toast-border-color);
  border-radius: var(--numbers-32);
  background-color: var(--toast-background-color);
  background-image:
    linear-gradient(to right, var(--toast-background-color) 20%, transparent 100%),
    repeating-linear-gradient(
      45deg,
      var(--toast-stripe-color),
      var(--toast-stripe-color) 1px,
      var(--toast-background-color) 1px,
      var(--toast-background-color) 15px
    );
  box-shadow:
    0 4px 18.2px -2px var(--toast-shadow-elevation),
    inset 0 0 1.1px 2px var(--toast-shadow-inset);
  color: var(--toast-text-color);
  gap: var(--numbers-12);

  & .icon {
    font-size: calc(13px * 1.5);
  }

  &.success .icon {
    fill: var(--background-positive);
  }

  &.error .icon {
    fill: var(--background-negative);
  }

  &.info .icon {
    fill: var(--background-info);
  }

  &.warning .icon {
    fill: var(--background-notice);
  }

  & .message {
    z-index: 2;
    display: flex;
    align-items: center;
    gap: var(--numbers-12);

    & .icon-wrapper {
      & .icon {
        position: relative;
        z-index: 2;
        display: flex;
        width: 20px;
        height: 20px;
        align-items: center;
        justify-content: center;
        padding: 1px;
      }

      & .countdown {
        position: relative;
        z-index: 1;

        & .background,
        & .foreground {
          position: absolute;
          top: 0;
          left: 0;
          width: 19px;
          height: 19px;
          padding: 3px; /* the border thickness */
          border-radius: 50%;
          transform: translate(0.5px, 0.5px);
        }

        & .background {
          background-color: var(--background-inverse);
        }

        & .foreground {
          animation-duration: 10s;
          animation-iteration-count: 1;
          animation-name: countdown;
          animation-play-state: paused;
          animation-timing-function: linear;
          background-color: white;
          mask:
            linear-gradient(#0000 0 0) content-box intersect,
            conic-gradient(#000 var(--progress), #0000 0);

          --progress: 0deg; /* control the progression */
        }
      }
    }
  }

  & .link {
    a,
    a:visited {
      color: var(--content-brand-2);
    }
  }

  & .actions {
    display: flex;
    flex-grow: 1;
    justify-content: right;
    gap: var(--numbers-12);
    pointer-events: auto;

    & button {
      border: none;
      background: none;
      color: var(--toast-dismiss-color);
      cursor: pointer;
    }
  }
}

@property --progress {
  inherits: false;
  initial-value: 0deg;
  syntax: "<angle>";
}

@keyframes countdown {
  from {
    --progress: 0deg;
  }

  to {
    --progress: 360deg;
  }
}
</style>
